import { createApi } from '@reduxjs/toolkit/query/react';
import { captureException } from '@sentry/react';
import {
  axiosBaseQuery,
  clearScheduledRefresh,
  logoutAndRedirectToLogin,
  scheduleTokenRefresh,
} from 'services/api';
import { withSilent } from 'services/api/toastConfig';
import { getRefreshToken, storeAuthTokens } from 'services/utils/accessToken';
import { actions as userActions } from 'store/modules/user';
import { REPORTABLE_STATUS_CODES } from 'utils/sentry';

const clientId =
  import.meta.env.VITE_CLIENT_ID ||
  import.meta.env.REACT_APP_CLIENT_ID;

// API module for handling  authentication
export const oauth2Api = createApi({
  reducerPath: 'oauth2Api',
  baseQuery: axiosBaseQuery({
    apiPath: '/oauth2/', // Backend OAuth2 is NOT under /api/ path
  }),
  endpoints: builder => ({
    // Authenticate with user & password and get access token
    login: builder.mutation({
      // Silent so that logging in outside the login page (e.g. after an invite signup) shows no
      // "Saving..." toast; the login page never shows it anyway
      query: ({ username, password }) => ({
        url: 'token/',
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        data: {
          grant_type: 'password',
          username,
          password,
          client_id: clientId,
        },
        ...withSilent(),
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // Store the tokens
          storeAuthTokens(data.access_token, data.refresh_token);
          // Schedule token refresh based on expiration time
          scheduleTokenRefresh(data.refresh_token, data.expires_in);
          // Fetch User
          dispatch(userActions.userFetchRequest());
        } catch (error) {
          console.log('Failed to get the token');
        }
      },
    }),
    // This endpoint has been implemented as a manual axios fetch to avoid a circular dependencies
    // issue with axiosBaseQuery, you can find it as `fetchNewAccessToken` in src/services/api.js
    // refreshToken: builder.mutation({}),

    // Logout by revoking the token and removing the access and refresh tokens from storage
    logout: builder.mutation({
      query: () => ({
        url: 'revoke_token/',
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        data: {
          token: getRefreshToken(),
          token_type_hint: 'refresh_token',
          client_id: clientId,
        },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          console.log('Failed to revoke the token');
          // Network/connectivity failures aren't actionable; logout proceeds regardless
          if (REPORTABLE_STATUS_CODES.includes(error?.error?.status)) {
            captureException(new Error('Logout failed: token revocation request error'), {
              extra: { originalError: error },
            });
          }
        } finally {
          // Remove access and refresh tokens from storage
          logoutAndRedirectToLogin();
          clearScheduledRefresh();
        }
      },
    }),
    getUserInfo: builder.query({
      query: () => ({ url: 'userinfo/' }),
    }),
  }),
});

export const { useLoginMutation, useLogoutMutation, useGetUserInfoQuery } = oauth2Api;
